import Combobox from "@/components/core/forms/combobox";
import { Form, FormField, FormGroup, FormItem, FormLabel, FormMessage } from "@/components/core/forms/form";
import { Input } from "@/components/core/forms/input";
import Select from "@/components/core/forms/select";
import { Textarea } from "@/components/core/forms/textarea";
import projectService from "@/features/dashboard/api/services/project-service";
import { type Checklist, checklistCategoryOptions, checklistSchema } from "@/features/dashboard/types/checklist";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";

interface ChecklistFormProps {
	checklist?: Checklist;
	onSubmit: (values: Checklist) => Promise<void>;
	isSubmitting?: boolean;
}

export function ChecklistForm({ checklist, onSubmit }: ChecklistFormProps) {
	const form = useForm<Checklist>({
		resolver: zodResolver(checklistSchema),
		defaultValues: checklist
			? {
					...checklist,
					project_id: checklist.project_id || null,
				}
			: {
					name: "",
					description: "",
					category: "Monthly Inspection Report",
					project_id: null,
				},
	});

	return (
		<Form {...form}>
			<form id="checklist-form" className="flex flex-col w-full gap-6" onSubmit={form.handleSubmit(onSubmit)}>
				<div className="flex flex-col w-full gap-6">
					<div className="grid grid-cols-12 w-full gap-x-4 gap-y-3">
						<div className="col-span-12">
							<FormField
								control={form.control}
								name="name"
								render={({ field }) => (
									<FormItem>
										<FormLabel required>Checklist Name</FormLabel>
										<FormGroup>
											<Input className="input" placeholder="Enter checklist name" {...field} />
										</FormGroup>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
					</div>

					<div className="grid grid-cols-12 w-full gap-x-4 gap-y-3">
						<div className="col-span-12">
							<FormField
								control={form.control}
								name="description"
								render={({ field }) => (
									<FormItem>
										<FormLabel>Description</FormLabel>
										<FormGroup>
											<Textarea className="input" placeholder="Enter checklist description" rows={3} {...field} value={field.value || ""} />
										</FormGroup>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
					</div>

					<div className="grid grid-cols-12 w-full gap-x-4 gap-y-3">
						<div className="col-span-6">
							<FormField
								control={form.control}
								name="category"
								render={({ field }) => (
									<FormItem>
										<FormLabel required>Category</FormLabel>
										<FormGroup>
											<Select placeholder="Select category" options={checklistCategoryOptions} defaultValue={field.value} onValueChange={field.onChange} />
										</FormGroup>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
						<div className="col-span-6">
							<FormField
								control={form.control}
								name="project_id"
								render={({ field }) => (
									<FormItem>
										<FormLabel>Project</FormLabel>
										<FormGroup>
											<Combobox
												defaultValue={field.value?.toString()}
												placeholderValue={
													checklist?.project_id && checklist?.project
														? {
																value: checklist.project_id.toString(),
																label: checklist.project.name,
															}
														: undefined
												}
												onValueChange={(value) => {
													field.onChange(value ? Number(value) : null);
												}}
												loadOptions={async (value: string) => {
													if (!value || value.length === 0) {
														// Load all projects when no search term
														const response = await projectService.list();
														return response.data.map((project) => ({
															label: project.name,
															value: project.id.toString(),
														}));
														// biome-ignore lint/style/noUselessElse: <explanation>
													} else {
														// Search projects when there's a search term
														const response = await projectService.search(value);
														return response.data.map((project) => ({
															label: project.name,
															value: project.id.toString(),
														}));
													}
												}}
												placeholder="Select a project..."
											/>
										</FormGroup>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
					</div>
				</div>
			</form>
		</Form>
	);
}
